import React, { useState, useMemo } from 'react';
import { MotifTable, MotifProgressLoader, MotifButton } from '@ey-xd/motif-react';
import Search from 'common/components/Search/Search';
import './TableComponent.scss';
import { MotifPaginationSelect } from '@ey-xd/motif-react';
import { MotifPaginationSelectItem } from '@ey-xd/motif-react';

interface TableComponentProps {
  breadCrumbs?: any;
  title: string;
  description?: string;
  columns: any[];
  columnActions?: any;
  data: any[];
  loading?: boolean;
  searchPlaceholder?: string;
  searchKeys: string[];
  tabCount?: number;
  actionButtonLabel?: string;
  onActionButtonClick?: () => void;
  emptyStateMessage?: string;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const TableComponent: React.FC<TableComponentProps> = ({
  breadCrumbs,
  title,
  description,
  columns,
  columnActions = {},
  data,
  loading = false,
  searchPlaceholder = 'Search...',
  searchKeys,
  tabCount,
  actionButtonLabel,
  onActionButtonClick,
  emptyStateMessage = 'No data to display',
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  const filteredData = useMemo(() => {
    if (!searchValue) return data;
    return data.filter((item) =>
      searchKeys.some((key) =>
        (item[key] || '').toString().toLowerCase().includes(searchValue.toLowerCase())
      )
    );
  }, [searchValue, data, searchKeys]);

  const displayData = useMemo(() => {
    if (filteredData.length === 0) {
      return [{ _noData: true }];
    }
    return filteredData;
  }, [filteredData]);

  // Hide column actions if the table is in "no data" state
  const isNoData = filteredData.length === 0;
  const tableComponents = useMemo(() => (isNoData ? {} : columnActions), [isNoData, columnActions]);

  const enhancedColumns = useMemo(() => {
    if (!columns.length) return columns;
    return columns.map((col, index) => {
      const originalRenderer = (col as any).cellRenderer;
      if (typeof originalRenderer === 'string') {
        return { ...col };
      }
      return {
        ...col,
        cellRenderer: (params: any) => {
          if (params.data?._noData && index === 0) {
            return (
              <div className="tableComponent__noData">
                {emptyStateMessage}
              </div>
            );
          }
          if (params.data?._noData) {
            return '';
          }
          return typeof originalRenderer === 'function' ? originalRenderer(params) : params.value;
        },
      };
    });
  }, [columns]);

  return (
    <div className="tableComponent__wrapper">
      <MotifProgressLoader show={loading} />
      <div className="breadCrumbs">{breadCrumbs}</div>
      <div className="tableComponent__headerBar">
        <div className="heading">
          <div className="title">{title}</div>
          {description && <div className="description">{description}</div>}
        </div>
        {actionButtonLabel && (
          <MotifButton
            variant="primary"
            size="medium"
            onClick={onActionButtonClick}
            className="tableComponent__actionBtn"
          >
            {actionButtonLabel}
          </MotifButton>
        )}
      </div>
      <div className="tableComponent__card">
        <div className="tableComponent__sectionBar">
          <div>
            <div className="tableComponent__sectionTitle">
              {title}
              {typeof tabCount === 'number' && (
                <span className="tableComponent__resultsBadge">{tabCount} Results</span>
              )}
            </div>
            <div className="tableComponent__sectionSubtext">
              { description }
            </div>
          </div>
          <Search
            value={searchValue}
            onChange={(e: any) => setSearchValue(e.target.value)}
            placeholder={searchPlaceholder}
            className="tableComponent__search"
          />
        </div>
        <div className="tableComponent__tableWrap">
          <MotifTable
            className="tableComponent__table"
            key={isNoData ? 'grid-empty' : 'grid-data'}
            columnDefs={enhancedColumns}
            rowData={displayData}
            defaultColDef={{ sortable: true, wrapText: true, autoHeight: true }}
            suppressCellFocus
            suppressRowClickSelection
            rowSelection="multiple"
            pagination
            paginationPageSize={pageSize}
            firstColumnBorderRight
            components={tableComponents}
            paginationSelect={
              <MotifPaginationSelect>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <MotifPaginationSelectItem
                    key={size}
                    onClick={() => setPageSize(size)}
                    selected={pageSize === size}
                  >
                    Show {size}
                  </MotifPaginationSelectItem>
                ))}
              </MotifPaginationSelect>}
          />
        </div>
      </div>
    </div>
  );
};

export default TableComponent;
